import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, Zap, Crown, Building2, Loader2, CheckCircle2, XCircle, History, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

export default function SubscriptionPage() {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const { language, formatCurrency } = useLanguage();
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [pollStatus, setPollStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled");

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        api.get("/public/plans"),
        api.get("/subscribe/current"),
      ]);
      setPlans(plansRes.data || []);
      setCurrentSub(subRes.data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); refreshUser(); }, [fetchData]);

  // Poll payment status on return from Stripe
  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPollStatus("timeout");
        return;
      }
      try {
        const res = await api.get(`/subscribe/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setPollStatus("success");
          toast.success(`Subscribed to ${res.data.plan_name}!`);
          fetchData();
          refreshUser();
          return;
        }
        if (res.data.status === "expired") {
          setPollStatus("expired");
          return;
        }
      } catch {}
      attempts++;
      setPollStatus("polling");
      setTimeout(poll, 2500);
    };
    poll();
  }, [sessionId, fetchData]);

  const handleSubscribe = async (plan) => {
    setCheckoutLoading(plan.id);
    try {
      const res = await api.post("/subscribe/checkout", {
        plan_id: plan.id,
        billing_cycle: billingCycle,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally {
      setCheckoutLoading(null);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get("/subscribe/history");
      setHistory(res.data.transactions || []);
      setShowHistory(true);
    } catch {
      toast.error("Failed to load payment history");
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You'll be moved to a grace period.")) return;
    setCancelling(true);
    try {
      const res = await api.post("/subscribe/cancel", { reason: '' });
      toast.success(res.data.message || "Subscription cancelled");
      fetchData();
      refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    } finally {
      setCancelling(false);
    }
  };

  const planIcons = { 0: Zap, 1: Crown, 2: Building2 };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="subscription-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl font-bold">Subscription</h1>
        <p className="text-muted-foreground text-sm mt-1">Choose the plan that works for you</p>
      </div>

      {/* Payment status banners */}
      {sessionId && pollStatus === "polling" && (
        <div className="mb-6 p-4 rounded-lg bg-primary/10 border border-primary/20 flex items-center gap-3" data-testid="poll-status-polling">
          <Loader2 className="animate-spin text-primary" size={20} />
          <span className="text-sm">Verifying your payment...</span>
        </div>
      )}
      {pollStatus === "success" && (
        <div className="mb-6 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-3" data-testid="poll-status-success">
          <CheckCircle2 className="text-emerald-500" size={20} />
          <span className="text-sm font-medium">Payment successful! Your subscription is now active.</span>
        </div>
      )}
      {pollStatus === "timeout" && (
        <div className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center gap-3" data-testid="poll-status-timeout">
          <Loader2 className="text-amber-500" size={20} />
          <span className="text-sm">Payment verification is taking longer than expected. Please check back shortly.</span>
        </div>
      )}
      {cancelled && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/20 flex items-center gap-3" data-testid="poll-status-cancelled">
          <XCircle className="text-destructive" size={20} />
          <span className="text-sm">Payment was cancelled. You can try again anytime.</span>
        </div>
      )}

      {/* Current subscription */}
      {currentSub && (
        <Card className="card-hover mb-8" data-testid="current-subscription-card">
          <CardContent className="py-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Current Plan</p>
                <p className="font-heading text-lg font-bold mt-0.5">{currentSub.subscription_plan || "Free"}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={currentSub.subscription_status === "ACTIVE" ? "default" : currentSub.subscription_status === "GRACE" ? "outline" : "destructive"} data-testid="subscription-status-badge">
                  {currentSub.subscription_status}
                </Badge>
                {currentSub.subscription_source === 'admin' && (
                  <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-500" data-testid="admin-override-badge">Admin Set</Badge>
                )}
              </div>
            </div>
            {currentSub.subscription_billing_cycle && (
              <p className="text-xs text-muted-foreground mt-2">
                Billing: {currentSub.subscription_billing_cycle} | Updated: {currentSub.subscription_updated_at ? new Date(currentSub.subscription_updated_at).toLocaleDateString(language) : "N/A"}
              </p>
            )}
            {currentSub.subscription_status === 'GRACE' && currentSub.grace_until && (
              <p className="text-xs text-amber-400 mt-1 font-medium">
                Active until {new Date(currentSub.grace_until).toLocaleDateString(language, { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
            {currentSub.subscription_status === 'SUSPENDED' && (
              <p className="text-xs text-red-400 mt-1 font-medium">
                Subscription ended. Upgrade to restore access.
              </p>
            )}
            <div className="flex items-center gap-2 mt-4">
              {currentSub.subscription_status === 'ACTIVE' && currentSub.subscription_plan !== 'Free' && (
                <Button variant="outline" size="sm" onClick={handleCancel} disabled={cancelling} className="text-destructive hover:text-destructive" data-testid="cancel-subscription-btn">
                  {cancelling ? <Loader2 size={14} className="animate-spin mr-1" /> : <AlertTriangle size={14} className="mr-1" />}
                  {cancelling ? "Cancelling..." : "Cancel Subscription"}
                </Button>
              )}
              {currentSub.subscription_status === 'GRACE' && currentSub.grace_until && (
                <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-400">Grace Period</Badge>
              )}
              <Button variant="ghost" size="sm" onClick={fetchHistory} data-testid="payment-history-btn">
                <History size={14} className="mr-1" /> Payment History
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment history */}
      {showHistory && (
        <Card className="mb-8" data-testid="payment-history-section">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">Payment History</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowHistory(false)}>Close</Button>
            </div>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground">No payment history yet.</p>
            ) : (
              <div className="space-y-2">
                {history.map((txn) => (
                  <div key={txn.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 border border-border/50" data-testid={`txn-${txn.id}`}>
                    <div>
                      <p className="text-sm font-medium">{txn.plan_name} <span className="text-muted-foreground font-normal">({txn.billing_cycle})</span></p>
                      <p className="text-xs text-muted-foreground">{txn.created_at ? new Date(txn.created_at).toLocaleString(language) : ''}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{formatCurrency(txn.amount, txn.currency || "USD")}</p>
                      <Badge variant={txn.payment_status === 'paid' ? 'default' : txn.payment_status === 'failed' ? 'destructive' : 'outline'} className="text-xs">
                        {txn.payment_status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Billing cycle toggle */}
      <div className="flex items-center justify-center gap-3 mb-8" data-testid="billing-cycle-toggle">
        <button
          onClick={() => setBillingCycle("monthly")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billingCycle === "monthly" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
          data-testid="billing-monthly-btn"
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingCycle("yearly")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${billingCycle === "yearly" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
          data-testid="billing-yearly-btn"
        >
          Yearly <span className="text-xs opacity-75">Save ~17%</span>
        </button>
      </div>

      {/* Plans grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map((plan, i) => {
          const price = billingCycle === "monthly" ? plan.monthly_price : plan.yearly_price;
          const isCurrent = currentSub?.subscription_plan_id === plan.id;
          const isFree = price <= 0;
          const PlanIcon = planIcons[i] || Zap;

          return (
            <Card key={plan.id} className={`card-hover relative ${i === 1 ? "border-primary/50 ring-1 ring-primary/20" : ""}`} data-testid={`plan-card-${plan.slug || plan.id}`}>
              {i === 1 && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground text-xs">Popular</Badge>
                </div>
              )}
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                    <PlanIcon className="text-primary" size={18} />
                  </div>
                  <CardTitle className="text-lg">{plan.name}</CardTitle>
                </div>
                <CardDescription className="text-sm">{plan.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-5">
                  <span className="text-3xl font-bold">{formatCurrency(price, plan.currency || "USD")}</span>
                  {!isFree && <span className="text-muted-foreground text-sm">/{billingCycle === "monthly" ? "mo" : "yr"}</span>}
                </div>
                <ul className="space-y-2.5 mb-6">
                  {(plan.features || []).map((feature, fi) => (
                    <li key={fi} className="flex items-center gap-2 text-sm">
                      <Check size={14} className="text-primary shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <Button className="w-full" variant="secondary" disabled data-testid={`plan-current-${plan.slug || plan.id}`}>
                    Current Plan
                  </Button>
                ) : isFree ? (
                  <Button className="w-full" variant="outline" disabled data-testid={`plan-free-${plan.slug || plan.id}`}>
                    Free Plan
                  </Button>
                ) : (
                  <Button
                    className="w-full btn-glow"
                    onClick={() => handleSubscribe(plan)}
                    disabled={!!checkoutLoading}
                    data-testid={`plan-subscribe-${plan.slug || plan.id}`}
                  >
                    {checkoutLoading === plan.id ? (
                      <Loader2 className="animate-spin mr-2" size={16} />
                    ) : null}
                    {checkoutLoading === plan.id ? "Redirecting..." : `Subscribe to ${plan.name}`}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
